import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  message,
  Row,
  Col,
  Card,
  Space,
  Typography,
  Divider,
  Spin,
  Empty,
  DatePicker,
  InputNumber,
  Modal,
  Tag,
} from "antd";
import {
  SearchOutlined,
  FileTextOutlined,
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  CarOutlined,
  DollarOutlined,
  CreditCardOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  SafetyOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import API from "../../api";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const { Option } = Select;
const { Title, Text } = Typography;
const { confirm } = Modal;

const RcInsurancePayment = () => {
  const [form] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stores, setStores] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [taxesLoading, setTaxesLoading] = useState(false);
  const [productModels, setProductModels] = useState([]);
  const [productModelsLoading, setProductModelsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  console.log("Search Results:", searchResults); // Debugging log
  const [user, setUser] = useState(null);
  const [showDownload, setShowDownload] = useState(false);
  const [lastSalesId, setLastSalesId] = useState(null);

  const [paymentData, setPaymentData] = useState({
    costPrice: 0,
    sellingPrice: 0,
    gstRate: 0,
    gstAmount: 0,
    totalAmount: 0,
    paidAmount: 0,
    balanceAmount: 0,
    cashAmount: 0,
    upiAmount: 0,
  });

  const productSettings = useSelector(
    (state) => state.productSetting?.productSetting
  );

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData"));
    setUser(userData);
    fetchStores();
    fetchTaxes();
    fetchProductModels();
  }, []);

  useEffect(() => {
    if (user && stores.length > 0) {
      if (user.role === "admin") {
        return;
      } else if (user.storeId) {
        const userStore = stores.find((store) => store._id === user.storeId);
        if (userStore) {
          form.setFieldsValue({ storeId: userStore._id });
        }
      } else if (stores.length === 1) {
        form.setFieldsValue({ storeId: stores[0]._id });
      }
    }
  }, [user, stores, form]);

  // New useEffect to set the invoice date in the paymentForm when searchResults changes
  useEffect(() => {
    if (searchResults?.sale?.orderDate) {
      paymentForm.setFieldsValue({
        date: dayjs(searchResults.sale.orderDate),
      });
    } else {
      paymentForm.setFieldsValue({
        date: dayjs(), // Fallback to current date if no invoice date is available
      });
    }
  }, [searchResults, paymentForm]);

  const fetchStores = async () => {
    try {
      const response = await API.get("/stores");
      setStores(response?.data || []);
    } catch (error) {
      message.error("Failed to fetch stores.");
    }
  };

  const fetchTaxes = async () => {
    setTaxesLoading(true);
    try {
      const response = await API.get("/taxes");
      if (response.status === 200) {
        setTaxes(response.data || []);
      }
    } catch (error) {
      message.error("Failed to fetch taxes.");
      setTaxes([]);
    } finally {
      setTaxesLoading(false);
    }
  };

  const fetchProductModels = async () => {
    setProductModelsLoading(true);
    try {
      const response = await API.get("/product-model?page=1&limit=1000");
      const { data } = response?.data || {};
      setProductModels(data || []);
    } catch (error) {
      message.error("Failed to fetch product models.");
      setProductModels([]);
    } finally {
      setProductModelsLoading(false);
    }
  };

  const handleSearch = async (values) => {
    if (!values.storeId) {
      message.error("Please select a store.");
      return;
    }
    if (!values.invoiceNo) {
      message.error("Please enter an invoice number.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await API.get(`/sales/search-invoice`, {
        params: {
          storeId: values.storeId,
          invoiceNo: values.invoiceNo,
        },
      });

      if (response.data && response.data.sale) {
        setSearchResults(response.data);
        message.success("Invoice found successfully!");
        paymentForm.setFieldsValue({
          particular: "RC Insurance",
        });
      } else {
        setSearchResults(null);
        message.warning("No invoice found with the given details.");
      }
    } catch (error) {
      message.error("Failed to search invoice. Please try again.");
      setSearchResults(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProductModelChange = (value) => {
    if (!value) {
      // Handle clear action: reset paymentData and form fields
      setPaymentData({
        costPrice: 0,
        sellingPrice: 0,
        gstRate: 0,
        gstAmount: 0,
        totalAmount: 0,
        paidAmount: 0,
        balanceAmount: 0,
        cashAmount: 0,
        upiAmount: 0,
      });
      paymentForm.setFieldsValue({
        costPrice: 0,
        sellingPrice: 0,
        gstAmount: 0,
        totalAmount: 0,
        paidAmount: 0,
        balanceAmount: 0,
        cashAmount: 0,
        upiAmount: 0,
      });
      return;
    }

    const selectedModel = productModels.find((model) => model._id === value);
    if (selectedModel) {
      const costPrice = selectedModel.costPrice || 0;
      const sellingPrice = selectedModel.sellingPrice || 0;
      const gstAmount = (sellingPrice * paymentData.gstRate) / 100;
      const totalAmount = sellingPrice + gstAmount;
      setPaymentData((prev) => ({
        ...prev,
        costPrice,
        sellingPrice,
        gstAmount,
        totalAmount,
        paidAmount: 0,
        balanceAmount: totalAmount,
        cashAmount: 0,
        upiAmount: 0,
      }));
      paymentForm.setFieldsValue({
        costPrice,
        sellingPrice,
        gstAmount: gstAmount.toFixed(2),
        totalAmount: totalAmount.toFixed(2),
        paidAmount: 0,
        balanceAmount: totalAmount.toFixed(2),
        cashAmount: 0,
        upiAmount: 0,
      });
    }
  };

  const calculateAmounts = (changedField, value) => {
    const newData = { ...paymentData };

    if (changedField === "sellingPrice") {
      newData.sellingPrice = value || 0;
      newData.gstAmount = (newData.sellingPrice * newData.gstRate) / 100;
      newData.totalAmount = newData.sellingPrice + newData.gstAmount;
      newData.paidAmount = 0;
      newData.balanceAmount = newData.totalAmount;
      newData.cashAmount = 0;
      newData.upiAmount = 0;
    } else if (changedField === "costPrice") {
      newData.costPrice = value || 0;
    } else if (changedField === "gstRate") {
      newData.gstRate = value || 0;
      newData.gstAmount = (newData.sellingPrice * newData.gstRate) / 100;
      newData.totalAmount = newData.sellingPrice + newData.gstAmount;
      newData.paidAmount = 0;
      newData.balanceAmount = newData.totalAmount;
      newData.cashAmount = 0;
      newData.upiAmount = 0;
    } else if (changedField === "paidAmount") {
      const validatedValue = Math.min(value || 0, newData.totalAmount);
      newData.paidAmount = validatedValue;
      newData.balanceAmount = newData.totalAmount - newData.paidAmount;
      if (newData.balanceAmount < 0) newData.balanceAmount = 0;
      newData.cashAmount = newData.paidAmount;
      newData.upiAmount = 0;
      if (value > newData.totalAmount) {
        message.warning(
          `Paid amount cannot exceed total amount of ₹${newData.totalAmount.toFixed(
            2
          )}`
        );
      }
    } else if (changedField === "cashAmount") {
      const validatedValue = Math.min(value || 0, newData.paidAmount);
      newData.cashAmount = validatedValue;
      newData.upiAmount = newData.paidAmount - newData.cashAmount;
      if (value > newData.paidAmount) {
        message.warning(
          `Cash amount cannot exceed paid amount of ₹${newData.paidAmount.toFixed(
            2
          )}`
        );
      }
    }

    setPaymentData(newData);

    paymentForm.setFieldsValue({
      gstAmount: newData.gstAmount.toFixed(2),
      totalAmount: newData.totalAmount.toFixed(2),
      paidAmount: newData.paidAmount.toFixed(2),
      balanceAmount: newData.balanceAmount.toFixed(2),
      cashAmount: newData.cashAmount.toFixed(2),
      upiAmount: newData.upiAmount.toFixed(2),
    });
  };

  const showPaymentConfirmation = (values) => {
    const customerName = searchResults?.sale?.customerId?.name || "N/A";
    const chassisNumber =
      searchResults.sale.products[0]?.tracking_number || "N/A";
    const totalAmount = paymentData.totalAmount || 0;
    const paidAmount = paymentData.paidAmount || 0;
    const balanceAmount = paymentData.balanceAmount || 0;

    const confirmationMessages = [
      `🚗 Ready to secure ${customerName}'s RC Insurance? This action will process ₹${totalAmount.toFixed(
        2
      )} for chassis ${chassisNumber}. Once confirmed, there's no turning back!`,
      `🛡️ About to finalize RC Insurance payment! Are you absolutely certain you want to proceed with ₹${totalAmount.toFixed(
        2
      )} transaction for ${customerName}?`,
      `⚡ Payment processing alert! You're about to complete RC Insurance payment of ₹${totalAmount.toFixed(
        2
      )}. This action is irreversible - proceed with confidence?`,
      `🎯 Final checkpoint reached! Ready to process RC Insurance payment for ${customerName}? Amount: ₹${totalAmount.toFixed(
        2
      )}. Confirm to proceed!`,
      `🔐 Security checkpoint: You're about to process a payment worth ₹${totalAmount.toFixed(
        2
      )}. Are you sure you want to complete this RC Insurance transaction?`,
    ];

    const randomMessage =
      confirmationMessages[
        Math.floor(Math.random() * confirmationMessages.length)
      ];

    confirm({
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SafetyOutlined style={{ color: "#faad14", fontSize: "20px" }} />
          <span style={{ fontSize: "18px", fontWeight: "600" }}>
            Confirm RC Insurance Payment
          </span>
        </div>
      ),
      icon: null,
      content: (
        <div style={{ padding: "16px 0" }}>
          <div
            style={{
              background: "linear-gradient(135deg, #fff7e6 0%, #fff2e8 100%)",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "16px",
              border: "1px solid #ffd591",
            }}
          >
            <Text
              style={{ fontSize: "16px", lineHeight: "1.6", color: "#8c4a2b" }}
            >
              {randomMessage}
            </Text>
          </div>
          <div
            style={{
              background: "#f6ffed",
              padding: "12px",
              borderRadius: "6px",
              border: "1px solid #b7eb8f",
            }}
          >
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={12}>
                <Text strong style={{ color: "#389e0d" }}>
                  Customer:
                </Text>
                <br />
                <Text style={{ color: "#52c41a" }}>{customerName}</Text>
              </Col>
              <Col xs={24} sm={12}>
                <Text strong style={{ color: "#389e0d" }}>
                  Chassis No:
                </Text>
                <br />
                <Text style={{ color: "#52c41a" }}>{chassisNumber}</Text>
              </Col>
              <Col xs={24} sm={12}>
                <Text strong style={{ color: "#389e0d" }}>
                  Total Amount:
                </Text>
                <br />
                <Text
                  style={{
                    color: "#52c41a",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                >
                  ₹{totalAmount.toFixed(2)}
                </Text>
              </Col>
              <Col xs={24} sm={12}>
                <Text strong style={{ color: "#389e0d" }}>
                  Payment Status:
                </Text>
                <br />
                <Text
                  style={{
                    color: balanceAmount > 0 ? "#fa8c16" : "#52c41a",
                    fontWeight: "600",
                  }}
                >
                  {balanceAmount > 0
                    ? `Partial (₹${paidAmount.toFixed(2)})`
                    : "Full Payment"}
                </Text>
              </Col>
            </Row>
          </div>
        </div>
      ),
      okText: (
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <CheckCircleOutlined />
          Yes, Process Payment
        </span>
      ),
      cancelText: (
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          Cancel
        </span>
      ),
      okButtonProps: {
        size: "large",
        style: {
          height: "40px",
          background: "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
          borderColor: "#52c41a",
          fontWeight: "600",
          boxShadow: "0 2px 8px rgba(82, 196, 26, 0.3)",
        },
      },
      cancelButtonProps: {
        size: "large",
        style: {
          height: "40px",
          borderColor: "#d9d9d9",
          color: "#8c8c8c",
        },
      },
      width: 600,
      centered: true,
      maskClosable: false,
      onOk() {
        return new Promise((resolve) => {
          handlePaymentSubmit(values).finally(() => {
            resolve();
          });
        });
      },
      onCancel() {
        message.info("Payment processing cancelled");
      },
    });
  };

  const handlePaymentSubmit = async (values) => {
    if (!searchResults) {
      message.error("Please search for an invoice first.");
      return;
    }

    setIsSubmitting(true);
    try {
      const invoiceNumber = `RCI-${Math.random()
        .toString(36)
        .substr(2, 9)
        .toUpperCase()}`;

      const paymentPayload = {
        customerId: searchResults.sale.customerId._id,
        // invoiceNumber: invoiceNumber,
        invoiceData: {
          invoiceId: searchResults.sale.invoiceId,
          orderDate: new Date(searchResults.sale.orderDate),
          totalAmount: searchResults.sale.totalAmount,
          status: searchResults.sale.status,
        },
        saleId: searchResults.sale._id,
        payment: {
          chassisNumber: searchResults.sale.products[0]?.tracking_number,
          productModelId: values.productModelId,
          date: values.date.toDate(),
          particular: values.particular || "RC Insurance",
          costPrice: parseFloat(values.costPrice) || 0,
          sellingPrice: parseFloat(values.sellingPrice) || 0,
          gstRate: parseFloat(values.gstRate) || 0,
          gstAmount: parseFloat(paymentData.gstAmount) || 0,
          totalAmount: parseFloat(paymentData.totalAmount) || 0,
          paidAmount: parseFloat(paymentData.paidAmount) || 0,
          balanceAmount: parseFloat(paymentData.balanceAmount) || 0,
          paymentMethods: {
            cashAmount: parseFloat(paymentData.cashAmount) || 0,
            upiAmount: parseFloat(paymentData.upiAmount) || 0,
          },
        },
        storeId: form.getFieldValue("storeId"),
        createdBy: user?._id,
        status: paymentData.balanceAmount > 0 ? "pending" : "completed",
      };

      const response = await API.post("/rc-insurance-payments", paymentPayload);

      if (response.data.success) {
        message.success({
          content:
            "🎉 RC Insurance payment processed successfully! Payment details have been recorded.",
          duration: 4,
        });
        paymentForm.resetFields();
        setPaymentData({
          costPrice: 0,
          sellingPrice: 0,
          gstRate: 0,
          gstAmount: 0,
          totalAmount: 0,
          paidAmount: 0,
          balanceAmount: 0,
          cashAmount: 0,
          upiAmount: 0,
        });
        setShowDownload(true);
        setLastSalesId(response.data.data?.saleId);
      } else {
        message.error(response.data.message || "Payment processing failed.");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Payment processing failed. Please try again.";
      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (values) => {
    showPaymentConfirmation(values);
  };

  const handleInvoiceDownload = async () => {
    try {
      const res = await API.get(
        `/rc-insurance-payments/generateInvoice/${lastSalesId}`
      );
      console.log("Invoice generation response:", res);
      if (res.status === 200) {
        message.success("Invoice generated successfully.");
        const getPdf = await API.get(res.data.downloadLink, {
          responseType: "arraybuffer",
        });
        const blob = new Blob([getPdf.data], { type: "application/pdf" });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `invoice_.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
        setShowDownload(false);
      }
    } catch (error) {
      message.error("Failed to download invoice");
    }
  };

  const formatNumber = (number) =>
    `₹${new Intl.NumberFormat("en-IN").format(number || 0)}`;

  return (
    <div style={{ padding: "20px", background: "#f5f5f5", minHeight: "100vh" }}>
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FileTextOutlined style={{ color: "#1890ff" }} />
            <span>RC Insurance Payment Search</span>
          </div>
        }
        style={{ marginBottom: "20px" }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSearch}
          initialValues={{
            storeId: user?.storeId || null,
          }}
        >
          <Row gutter={[8, 8]}>
            <Col xs={24} sm={8} md={6}>
              <Form.Item
                name="storeId"
                label="Select Store/Branch"
                rules={[{ required: true, message: "Please select a store" }]}
              >
                <Select
                  placeholder={
                    user?.role === "admin"
                      ? "Select Store"
                      : "Store (Auto-selected)"
                  }
                  disabled={user?.role !== "admin" || stores.length === 1}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {stores.length === 0 ? (
                    <Option disabled>No stores available</Option>
                  ) : (
                    stores.map((store) => (
                      <Option key={store._id} value={store._id}>
                        {store.name}
                      </Option>
                    ))
                  )}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Form.Item
                name="invoiceNo"
                label="Enter Invoice No"
                rules={[
                  { required: true, message: "Please enter invoice number" },
                ]}
              >
                <Input
                  placeholder="Enter Invoice Number"
                  prefix={<SearchOutlined />}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Form.Item label=" " style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                  icon={<SearchOutlined />}
                  block
                >
                  Search Invoice
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
        
        {/* Warning message for non-bike invoices */}
        {searchResults && searchResults.sale && searchResults.sale.totalAmount < 50000 && (
          <div style={{ 
            marginTop: '16px',
            padding: '12px 16px',
            backgroundColor: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: '6px',
            textAlign: 'center'
          }}>
            <Text style={{ 
              color: '#ff4d4f', 
              fontSize: '14px', 
              fontWeight: '500',
              display: 'block'
            }}>
              ⚠️ This invoice seems not a Bike Invoice
            </Text>
          </div>
        )}
      </Card>

      {searchResults && (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={10}>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <Card
                title={
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <UserOutlined style={{ color: "#52c41a" }} />
                      <span>Customer & Invoice Information</span>
                    </div>
                    <div>
                      {searchResults.sale?.rcInsuranceStatus !== "" ? (
                        <Tag
                          color={
                            searchResults.sale?.rcInsuranceStatus === "pending"
                              ? "warning"
                              : "success"
                          }
                        >
                          {searchResults.sale?.rcInsuranceStatus}
                        </Tag>
                      ) : null}
                    </div>
                  </div>
                }
              >
                <Row gutter={[24, 24]}>
                  <Col xs={24} sm={12}>
                    <div style={{ marginBottom: "8px" }}>
                      <Title
                        level={5}
                        style={{
                          margin: "0 0 8px 0",
                          color: "#1890ff",
                        }}
                      >
                        Customer Details
                      </Title>
                      <Space
                        direction="vertical"
                        size="small"
                        style={{ width: "100%" }}
                      >
                        <div>
                          <Text strong>Name:</Text>
                          <br />
                          <Text>
                            {searchResults.sale.customerId?.name || "N/A"}
                          </Text>
                        </div>
                        <div>
                          <Text strong>Phone:</Text>
                          <br />
                          <Text>
                            <PhoneOutlined style={{ marginRight: "4px" }} />
                            {searchResults.sale.customerId?.phone || "N/A"}
                          </Text>
                        </div>
                        <div>
                          <Text strong>Address:</Text>
                          <br />
                          <Text>
                            <EnvironmentOutlined
                              style={{ marginRight: "4px" }}
                            />
                            {searchResults.sale.customerId?.address
                              ? `${
                                  searchResults.sale.customerId.address
                                    .street || ""
                                }, ${
                                  searchResults.sale.customerId.address.city ||
                                  ""
                                }, ${
                                  searchResults.sale.customerId.address.state ||
                                  ""
                                } - ${
                                  searchResults.sale.customerId.address
                                    .pincode || ""
                                }`
                              : "N/A"}
                          </Text>
                        </div>
                      </Space>
                    </div>
                  </Col>

                  <Col xs={24} sm={12}>
                    <div style={{ marginBottom: "8px" }}>
                      <Title
                        level={5}
                        style={{ margin: "0 0 8px 0", color: "#722ed1" }}
                      >
                        Invoice Details
                      </Title>
                      <Space
                        direction="vertical"
                        size="small"
                        style={{ width: "100%" }}
                      >
                        <div>
                          <Text strong>Invoice Number:</Text>
                          <br />
                          <Text>{searchResults.sale.invoiceId || "N/A"}</Text>
                        </div>
                        <div>
                          <Text strong>Invoice Date:</Text>
                          <br />
                          <Text>
                            {searchResults.sale.orderDate
                              ? new Date(
                                  searchResults.sale.orderDate
                                ).toLocaleDateString()
                              : "N/A"}
                          </Text>
                        </div>
                        <div>
                          <Text strong>Total Amount:</Text>
                          <br />
                          <Text
                            style={{
                              fontSize: "16px",
                              color: "#1890ff",
                              fontWeight: "600",
                            }}
                          >
                            {formatNumber(searchResults.sale.totalAmount)}
                          </Text>
                        </div>
                        <div>
                          <Text strong>Status:</Text>
                          <br />
                          <Text
                            style={{
                              color:
                                searchResults.sale.status === "completed"
                                  ? "#52c41a"
                                  : searchResults.sale.status === "pending"
                                  ? "#faad14"
                                  : "#ff4d4f",
                              fontWeight: "600",
                            }}
                          >
                            {searchResults.sale.status?.toUpperCase() || "N/A"}
                          </Text>
                        </div>
                        <div>
                          <Text strong>Finance:</Text>
                          <br />
                          <Text strong style={{ 
                            textTransform: 'uppercase', 
                            backgroundColor: (searchResults.sale?.finance || "CASH").toUpperCase() === "CASH" ? 'green' : 'red', 
                            color: 'white', 
                            padding: '4px 8px', 
                            borderRadius: '4px' 
                          }}>{(searchResults.sale?.finance || "CASH").toUpperCase()}</Text>
                        </div>
                      </Space>
                    </div>
                  </Col>
                </Row>
                <Divider style={{ margin: "8px 0" }} />
                <div>
                  <Title
                    level={5}
                    style={{ margin: "0 0 8px 0", color: "#fa8c16" }}
                  >
                    <CarOutlined style={{ marginRight: "8px" }} />
                    Vehicle Information
                  </Title>
                  {searchResults.sale.products &&
                    searchResults.sale.products.length > 0 && (
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                          <Text strong>Product:</Text>
                          <br />
                          <Text>
                            {searchResults.sale.products[0]?.productId
                              ?.specifications?.name || "N/A"}
                          </Text>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Text strong>Chassis No:</Text> <br />
                          <Text>
                            {searchResults.sale.products[0]?.tracking_number ||
                              "N/A"}
                          </Text>
                        </Col>
                      </Row>
                    )}
                </div>
              </Card>
            </Space>
          </Col>

          <Col xs={24} lg={14}>
            <Card
              title={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <CreditCardOutlined style={{ color: "#f5222d" }} />
                  <span>RC Insurance Payment Form</span>
                </div>
              }
            >
              <Form
                form={paymentForm}
                layout="vertical"
                onFinish={handleFormSubmit}
                initialValues={{
                  date: searchResults?.sale?.orderDate
                    ? dayjs(searchResults.sale.orderDate)
                    : dayjs(),
                  particular: "RC Insurance",
                  productModelId: null,
                  costPrice: 0,
                  sellingPrice: 0,
                  gstAmount: 0,
                  totalAmount: 0,
                  paidAmount: 0,
                  balanceAmount: 0,
                  cashAmount: 0,
                  upiAmount: 0,
                }}
              >
                <Row gutter={[8, 8]}>
                  <Col xs={24} sm={6}>
                    <Form.Item
                      name="date"
                      label="Date"
                      rules={[
                        { required: true, message: "Please select date" },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <DatePicker
                        style={{ width: "100%" }}
                        format="DD/MM/YYYY"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Form.Item
                      name="particular"
                      label="Particular"
                      rules={[
                        { required: true, message: "Please enter particular" },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input placeholder="RC Insurance" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Form.Item
                      name="productModelId"
                      label="Product Model"
                      rules={[
                        {
                          required: true,
                          message: "Please select product model",
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder="Select Product Model"
                        loading={productModelsLoading}
                        onChange={handleProductModelChange}
                        showSearch
                        optionFilterProp="children"
                        allowClear
                        filterOption={(input, option) =>
                          option?.children
                            ?.toLowerCase()
                            ?.includes(input.toLowerCase())
                        }
                      >
                        {productModels?.map((model) => (
                          <Option key={model._id} value={model._id}>
                            {model.productModelNumber}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Form.Item
                      name="sellingPrice"
                      label="Selling Price"
                      rules={[
                        {
                          required: true,
                          message: "Please enter selling price",
                        },
                        {
                          type: "number",
                          min: 0,
                          message: "Selling price must be greater than 0",
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        placeholder="Enter Selling Price"
                        prefix="₹"
                        min={0}
                        precision={2}
                        onChange={(value) =>
                          calculateAmounts("sellingPrice", value)
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Form.Item
                      name="gstRate"
                      label="GST (%)"
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder="Select GST Rate"
                        loading={taxesLoading}
                        onChange={(value) => calculateAmounts("gstRate", value)}
                        showSearch
                        optionFilterProp="children"
                        allowClear
                        filterOption={(input, option) =>
                          option?.children
                            ?.toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        <Option value="">-- Select GST Rate --</Option>
                        {taxes.map((tax) => (
                          <Option
                            key={tax._id}
                            value={
                              tax.taxPercentage || tax.rate || tax.percentage
                            }
                          >
                            {tax.taxName || tax.name} (
                            {tax.taxPercentage || tax.rate || tax.percentage}
                            %)
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Form.Item
                      name="gstAmount"
                      label="GST Amount"
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        prefix="₹"
                        readOnly
                        style={{ backgroundColor: "#f5f5f5" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Form.Item
                      name="totalAmount"
                      label="Total Amount"
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        prefix="₹"
                        readOnly
                        style={{
                          backgroundColor: "#e6f7ff",
                          fontWeight: "600",
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Divider style={{ margin: "12px 0" }} />
                <Row gutter={[8, 8]}>
                  <Col xs={24}>
                    <Title
                      level={5}
                      style={{ margin: "0 0 12px 0", color: "#52c41a" }}
                    >
                      <DollarOutlined style={{ marginRight: "8px" }} />
                      Payment Details
                    </Title>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="paidAmount"
                      label={`Paid Amount ${
                        paymentData.totalAmount > 0
                          ? `(Max: ₹${paymentData.totalAmount.toFixed(2)})`
                          : ""
                      }`}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        prefix="₹"
                        min={0}
                        max={paymentData.totalAmount}
                        precision={2}
                        onChange={(value) =>
                          calculateAmounts("paidAmount", value)
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="balanceAmount"
                      label="Balance Amount"
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        prefix="₹"
                        readOnly
                        style={{
                          backgroundColor:
                            paymentData.balanceAmount > 0
                              ? "#fff2e8"
                              : "#f6ffed",
                          fontWeight: "600",
                          color:
                            paymentData.balanceAmount > 0
                              ? "#fa8c16"
                              : "#52c41a",
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="cashAmount"
                      label={`Cash Amount ${
                        paymentData.paidAmount > 0
                          ? `(Max: ₹${paymentData.paidAmount.toFixed(2)})`
                          : ""
                      }`}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        prefix="₹"
                        min={0}
                        max={paymentData.paidAmount}
                        precision={2}
                        onChange={(value) =>
                          calculateAmounts("cashAmount", value)
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="upiAmount"
                      label="UPI Amount"
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        prefix="₹"
                        readOnly
                        style={{
                          backgroundColor: "#f0f5ff",
                          fontWeight: "600",
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Divider style={{ margin: "12px 0" }} />
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={isSubmitting}
                    icon={<CreditCardOutlined />}
                    style={{ height: "48px", fontSize: "16px" }}
                  >
                    Process RC Insurance Payment
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>
        </Row>
      )}

      {!searchResults && !isLoading && (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Search for an invoice to begin RC Insurance payment processing"
          />
        </Card>
      )}
      {isLoading && (
        <Card style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
          <div style={{ marginTop: "16px" }}>
            <Text>Searching for invoice...</Text>
          </div>
        </Card>
      )}

      <Modal
        title="Payment Successful"
        open={showDownload}
        onCancel={() => setShowDownload(false)}
        centered
        footer={[
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            size="large"
            onClick={handleInvoiceDownload}
          >
            Download RC Insurance Invoice
          </Button>,
        ]}
        maskClosable={false}
      >
        <p>
          Your payment is successful. Download the RC Insurance Invoice now.
        </p>
      </Modal>
    </div>
  );
};

export default RcInsurancePayment;
